import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { format } from "date-fns";

import { getSingleEmployeeQueryOptions } from "@/config/queries/query-options";
import { Button } from "@/components/ui/button/button";
import { Separator } from "@/components/ui/separator/separator";
import { AttendanceAndCheckins } from "./_components/attendance-and-checkins";
import { LoanSection } from "./_components/loans";
import { PerformanceSection } from "./_components/performance";
import { TodosSection } from "./_components/todos";
import { fetchAggregatePerformancePointsForEmployee } from "@/config/queries/admin/admin-queries";
import { AlertCircle } from "lucide-react";
import { LeadReportsSection } from "./_components/lead-reports";
// import { AnnouncementSection } from "./_components/announcement";

const SingleEmployeeProfile = () => {
  const adminKey = Cookies.get("key") as string;

  const { empId } = useParams() as {
    empId: string;
  };

  const { data, isPending } = useQuery(
    getSingleEmployeeQueryOptions(adminKey, empId)
  );

  const { data: EmployeePerformancePoints } = useQuery({
    queryKey: ["performance-agg-points"],
    queryFn: () =>
      fetchAggregatePerformancePointsForEmployee(adminKey)({
        employee: empId,
      }),
    enabled: empId !== "",
    refetchOnWindowFocus: false,
    retry: 2,
  });

  if (isPending) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-5 md:h-[calc(100%-56px)] h-[calc(100vh-56px)] relative">
      <div className="flex flex-row items-center justify-between">
        <div className="p-4 flex gap-4">
          {data?.employee?.photo ? (
            <div className="relative w-20 rounded-md">
              <img
                src={data?.employee?.photo}
                alt="Uploaded Image"
                className="object-cover max-w-full rounded-md"
              />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-accent flex items-center justify-center text-xl">
              {data?.employee?.name?.charAt(0)}
            </div>
          )}

          <div className="flex flex-col self-center">
            <h3 className="text-2xl font-semibold">{data?.employee?.name}</h3>
            <p className="text-sm">{data?.employee?.email}</p>
          </div>

          <div className="border-l flex flex-col px-4 gap-1">
            <div className="flex items-center gap-1 text-xl">
              <p>Performance Points of this month :</p>
              <p className="text-xl font-semibold">
                {data?.employee?.performancePoints} ({" "}
                {format(new Date(), "MMMM")} )
              </p>
            </div>
            <div className="flex items-center gap-1">
              <p>Last 3 months average score :</p>
              <p className="font-semibold">
                {EmployeePerformancePoints?.average?.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="border-l flex flex-col px-4 gap-1">
            <div className="flex items-center gap-1">
              <p>Total Paid Leaves :</p>
              <p className="font-semibold">{data?.employee?.paidLeavesTotal}</p>
            </div>
            <div className="flex items-center gap-1">
              <p>Remaining Paid Leaves :</p>
              <p className="font-semibold">
                {data?.employee?.paidLeavesRemaining}
              </p>
            </div>
          </div>
        </div>

        <Button asChild size="sm">
          <Link to={`/admin/employees/${empId}`}>Edit Profile</Link>
        </Button>
      </div>

      <Separator className="mb-5" />

      {data?.employee?.blocked && (
        <div className="my-5 p-2 rounded-lg bg-red-500/55 flex items-center gap-3">
          <AlertCircle className="h-5 w-5" />
          <p>This Employee has been blocked</p>
        </div>
      )}

      <div className="flex flex-col gap-8">
        <AttendanceAndCheckins />

        <LoanSection />

        <PerformanceSection />

        <TodosSection />

        {/* <AnnouncementSection /> */}
        <LeadReportsSection />

        <div className="mb-10" />
      </div>
    </div>
  );
};

export default SingleEmployeeProfile;
